import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { useAlert } from '../context/AlertContext';
import './Commands.css';

function Commands() {
  const [commands, setCommands] = useState([]);
  const [editingCommand, setEditingCommand] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all');
  const alert = useAlert();

  useEffect(() => {
    loadCommands();
  }, []);

  const loadCommands = async () => {
    try {
      const { data } = await axios.get('/api/commands');
      setCommands(data);
    } catch (error) {
      alert.error('Fehler beim Laden der Commands');
    }
  };

  const saveCommand = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const commandData = {
      name: formData.get('name'),
      response: formData.get('response'),
      cooldown: parseInt(formData.get('cooldown')),
      permission_level: formData.get('permission_level') || 'everyone',
      enabled_twitch_main: formData.get('enabled_twitch_main') ? 1 : 0,
      enabled_twitch_247: formData.get('enabled_twitch_247') ? 1 : 0,
      enabled_youtube: formData.get('enabled_youtube') ? 1 : 0,
      enabled_discord: formData.get('enabled_discord') ? 1 : 0,
      mirror_twitch_247: formData.get('mirror_twitch_247') ? 1 : 0,
      mirror_youtube: formData.get('mirror_youtube') ? 1 : 0
    };

    try {
      if (editingCommand) {
        await axios.put(`/api/commands/${editingCommand.id}`, commandData);
        alert.success('Command erfolgreich aktualisiert!');
      } else {
        await axios.post('/api/commands', commandData);
        alert.success('Command erfolgreich erstellt!');
      }

      setShowModal(false);
      setEditingCommand(null);
      loadCommands();
    } catch (error) {
      alert.error('Fehler beim Speichern des Commands');
    }
  };

  const deleteCommand = async (id) => {
    if (window.confirm('Command wirklich löschen?')) {
      try {
        await axios.delete(`/api/commands/${id}`);
        alert.success('Command gelöscht');
        loadCommands();
      } catch (error) {
        alert.error('Fehler beim Löschen des Commands');
      }
    }
  };

  const openModal = (command = null) => {
    setEditingCommand(command);
    setShowModal(true);
  };

  const filterCommands = (cmds, isCustom) => {
    return cmds
      .filter(cmd => cmd.is_custom === (isCustom ? 1 : 0))
      .filter(cmd => {
        // Search filter
        if (searchTerm && !cmd.name.toLowerCase().includes(searchTerm.toLowerCase())) {
          return false;
        }
        
        // Platform filter
        if (platformFilter !== 'all') {
          const platformKey = `enabled_${platformFilter.replace('-', '_')}`;
          if (cmd[platformKey] !== 1) {
            return false;
          }
        }
        
        return true;
      });
  };

  const builtInCommands = filterCommands(commands, false);
  const customCommands = filterCommands(commands, true);

  const getPermissionBadge = (level) => {
    const badges = {
      everyone: { text: 'Alle', class: 'perm-everyone' },
      mod: { text: 'Mod', class: 'perm-mod' },
      admin: { text: 'Admin', class: 'perm-admin' }
    };
    return badges[level] || badges.everyone;
  };

  return (
    <div className="commands-page">
      <div className="page-header">
        <h2>Commands</h2>
        <button className="btn-success" onClick={() => openModal()}>+ Neuer Command</button>
      </div>

      <div className="filters-section">
        <input
          type="text"
          className="search-input"
          placeholder="Command suchen..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select 
          className="platform-filter"
          value={platformFilter}
          onChange={(e) => setPlatformFilter(e.target.value)}
        >
          <option value="all">Alle Plattformen</option>
          <option value="twitch-main">Twitch Main</option>
          <option value="twitch-247">Twitch 24/7</option>
          <option value="youtube">YouTube</option>
          <option value="discord">Discord</option>
        </select>
      </div>

      {builtInCommands.length > 0 && (
        <div className="commands-section">
          <h3 className="section-title">Built-in Commands</h3>
          <div className="commands-list">
            {builtInCommands.map((cmd) => (
              <div key={cmd.id} className="command-card">
                <div className="command-header">
                  <div className="command-title-row">
                    <h3>!{cmd.name}</h3>
                    <span className="badge badge-builtin">Built-in</span>
                  </div>
                  <span className={`permission-badge ${getPermissionBadge(cmd.permission_level).class}`}>
                    {getPermissionBadge(cmd.permission_level).text}
                  </span>
                </div>
                
                <p className="command-response">{cmd.response}</p>
                
                <div className="command-platforms">
                  {cmd.enabled_twitch_main === 1 && <span className="platform-badge twitch">Twitch Main</span>}
                  {cmd.enabled_twitch_247 === 1 && <span className="platform-badge twitch">Twitch 24/7</span>}
                  {cmd.enabled_youtube === 1 && <span className="platform-badge youtube">YouTube</span>}
                  {cmd.enabled_discord === 1 && <span className="platform-badge discord">Discord</span>}
                </div>

                <div className="command-actions">
                  <button onClick={() => openModal(cmd)}>Bearbeiten</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {customCommands.length > 0 && (
        <div className="commands-section">
          <h3 className="section-title">Custom Commands</h3>
          <div className="commands-list">
            {customCommands.map((cmd) => (
              <div key={cmd.id} className="command-card">
                <div className="command-header">
                  <div className="command-title-row">
                    <h3>!{cmd.name}</h3>
                  </div>
                  <span className={`permission-badge ${getPermissionBadge(cmd.permission_level).class}`}>
                    {getPermissionBadge(cmd.permission_level).text}
                  </span>
                </div>
                
                <p className="command-response">{cmd.response}</p>
                
                <div className="command-platforms">
                  {cmd.enabled_twitch_main === 1 && <span className="platform-badge twitch">Twitch Main</span>}
                  {cmd.enabled_twitch_247 === 1 && <span className="platform-badge twitch">Twitch 24/7</span>}
                  {cmd.enabled_youtube === 1 && <span className="platform-badge youtube">YouTube</span>}
                  {cmd.enabled_discord === 1 && <span className="platform-badge discord">Discord</span>}
                </div>

                <div className="command-actions">
                  <button onClick={() => openModal(cmd)}>Bearbeiten</button>
                  <button className="btn-danger" onClick={() => deleteCommand(cmd.id)}>Löschen</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {builtInCommands.length === 0 && customCommands.length === 0 && (
        <div className="no-results">
          <p>Keine Commands gefunden</p>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editingCommand ? 'Command bearbeiten' : 'Neuer Command'}</h3>
            
            <form onSubmit={saveCommand}>
              <div className="form-group">
                <label>Command Name</label>
                <input 
                  name="name" 
                  defaultValue={editingCommand?.name} 
                  placeholder="z.B. discord" 
                  required 
                />
              </div>

              <div className="form-group">
                <label>Response</label>
                <textarea 
                  name="response" 
                  defaultValue={editingCommand?.response} 
                  placeholder="Command Antwort..." 
                  rows="3"
                  required 
                />
              </div>

              <div className="form-group">
                <label>Cooldown (Sekunden)</label>
                <input 
                  type="number" 
                  name="cooldown" 
                  defaultValue={editingCommand?.cooldown || 5} 
                  min="0"
                />
              </div>

              <div className="form-group">
                <label>Berechtigung</label>
                <select 
                  name="permission_level" 
                  defaultValue={editingCommand?.permission_level || 'everyone'}
                >
                  <option value="everyone">Alle</option>
                  <option value="mod">Moderatoren</option>
                  <option value="admin">Admin/Broadcaster</option>
                </select>
              </div>

              <div className="form-group">
                <label>Aktiviert auf:</label>
                <div className="checkbox-group">
                  <label>
                    <input 
                      type="checkbox" 
                      name="enabled_twitch_main" 
                      defaultChecked={editingCommand?.enabled_twitch_main === 1}
                    />
                    Twitch Main
                  </label>
                  <label>
                    <input 
                      type="checkbox" 
                      name="enabled_twitch_247" 
                      defaultChecked={editingCommand?.enabled_twitch_247 === 1}
                    />
                    Twitch 24/7
                  </label>
                  <label>
                    <input 
                      type="checkbox" 
                      name="enabled_youtube" 
                      defaultChecked={editingCommand?.enabled_youtube === 1}
                    />
                    YouTube
                  </label>
                  <label>
                    <input 
                      type="checkbox" 
                      name="enabled_discord" 
                      defaultChecked={editingCommand?.enabled_discord === 1}
                    />
                    Discord
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>Wenn auf Twitch Main getriggert, auch senden auf:</label>
                <div className="checkbox-group">
                  <label>
                    <input 
                      type="checkbox" 
                      name="mirror_twitch_247" 
                      defaultChecked={editingCommand?.mirror_twitch_247 === 1}
                    />
                    Twitch 24/7
                  </label>
                  <label>
                    <input 
                      type="checkbox" 
                      name="mirror_youtube" 
                      defaultChecked={editingCommand?.mirror_youtube === 1}
                    />
                    YouTube
                  </label>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)}>Abbrechen</button>
                <button type="submit" className="btn-success">Speichern</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Commands;
